package fr.frux.feature.treedetail.move

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import fr.frux.core.designsystem.components.DuplicateNearbyBanner
import fr.frux.core.designsystem.components.FruxButton
import fr.frux.core.designsystem.components.FruxButtonStyle
import fr.frux.core.designsystem.icons.FruxSpeciesGlyphs
import fr.frux.core.designsystem.icons.glyphFor
import fr.frux.core.designsystem.icons.legendColor
import fr.frux.core.designsystem.icons.speciesFamilyKey
import fr.frux.core.designsystem.layout.FruxLayoutMode
import fr.frux.core.designsystem.layout.LocalFruxLayoutMode
import fr.frux.core.designsystem.theme.fruxColors
import fr.frux.core.maps.Location
import fr.frux.feature.treedetail.resources.Res
import fr.frux.feature.treedetail.resources.treedetail_move_cancel
import fr.frux.feature.treedetail.resources.treedetail_move_duplicate_confirm
import fr.frux.feature.treedetail.resources.treedetail_move_duplicate_subtitle
import fr.frux.feature.treedetail.resources.treedetail_move_duplicate_title
import fr.frux.feature.treedetail.resources.treedetail_move_title
import fr.frux.feature.treedetail.resources.treedetail_move_title_expanded
import fr.frux.shared.content.TreeCorrectionLimits
import fr.frux.shared.domain.model.DuplicateCandidate
import org.jetbrains.compose.resources.stringResource

private val HEADER_SIDE_SLOT = 104.dp
private val PANEL_CORNER = 28.dp

/**
 * Déplacement d'un arbre sur la carte persistante en mode visée. [aim] est démarrée sur la
 * position actuelle de l'arbre, avec le cercle de [TreeCorrectionLimits.MAX_MOVE_METERS], et
 * arrêtée quand l'écran quitte la composition. Le retour système dépile sans dialogue : rien n'est
 * saisi qui mériterait d'être retenu.
 *
 * [onDone] est appelé après l'enregistrement, après le signalement de disparition et sur
 * « Annuler ». [locateButton] est fourni par composeApp, qui seul connaît le bouton de
 * localisation de la carte.
 */
@Composable
fun MoveTreeScreen(
    args: MoveTreeArgs,
    aim: TreeMoveAim,
    locateButton: @Composable () -> Unit,
    onDone: () -> Unit,
    onAddHere: (Location) -> Unit,
    onOpenTree: (Long) -> Unit,
    modifier: Modifier = Modifier,
    viewModel: MoveTreeViewModel = rememberMoveTreeViewModel(args),
) {
    val state by viewModel.state.collectAsState()
    val currentAim by rememberUpdatedState(aim)
    val currentOnDone by rememberUpdatedState(onDone)

    LaunchedEffect(viewModel) {
        viewModel.events.collect { currentOnDone() }
    }

    DisposableEffect(Unit) {
        currentAim.start(
            center = Location(args.latitude, args.longitude),
            radiusMeters = TreeCorrectionLimits.MAX_MOVE_METERS,
            scientificName = args.scientificName,
            excludedTreeId = args.treeId,
        )
        onDispose { currentAim.stop() }
    }

    val panel =
        MovePanelState(
            canReportGone = args.canReportGone,
            distanceMeters = aim.distanceMeters,
            saving = state.saving,
            reporting = state.reporting,
            error = state.error,
        )
    val actions =
        MovePanelActions(
            onSave = { aim.currentTarget()?.let(viewModel::save) },
            onAddHere = { aim.currentTarget()?.let(onAddHere) },
            onReportGone = viewModel::reportGone,
            onRecenter = {
                viewModel.consumeError()
                aim.recenter()
            },
        )

    if (LocalFruxLayoutMode.current == FruxLayoutMode.Expanded) {
        MoveTreeExpanded(args, aim.candidate, panel, actions, viewModel::cancel, onOpenTree, modifier)
    } else {
        MoveTreeCompact(args, aim.candidate, panel, actions, locateButton, viewModel::cancel, onOpenTree, modifier)
    }
}

/**
 * Téléphone : overlay transparent au-dessus de la carte persistante. Ni `Surface` ni `Scaffold`
 * plein écran (ils absorbent les gestes de la carte) : seuls l'en-tête, le bandeau et la feuille
 * basse captent le pointeur.
 */
@Composable
private fun MoveTreeCompact(
    args: MoveTreeArgs,
    candidate: DuplicateCandidate?,
    panel: MovePanelState,
    actions: MovePanelActions,
    locateButton: @Composable () -> Unit,
    onCancel: () -> Unit,
    onOpenTree: (Long) -> Unit,
    modifier: Modifier = Modifier,
) {
    Box(modifier = modifier.fillMaxSize()) {
        Column(modifier = Modifier.align(Alignment.TopCenter)) {
            MoveHeader(args.speciesName, onCancel)
            if (candidate != null) {
                MoveDuplicateBanner(candidate, onOpenTree, Modifier.padding(12.dp))
            }
        }
        Column(modifier = Modifier.align(Alignment.BottomCenter)) {
            // Hors du cercle, la feuille propose « Revenir dans le cercle » : la localisation
            // ferait double emploi (maquette DeplacerTropLoin).
            if (!panel.tooFar) {
                Box(
                    modifier = Modifier.fillMaxWidth().padding(end = 16.dp, bottom = 12.dp),
                    contentAlignment = Alignment.CenterEnd,
                ) { locateButton() }
            }
            Surface(
                modifier = Modifier.fillMaxWidth(),
                shape = RoundedCornerShape(topStart = PANEL_CORNER, topEnd = PANEL_CORNER),
                color = MaterialTheme.colorScheme.surface,
                shadowElevation = 4.dp,
            ) {
                MovePanel(
                    panel = panel,
                    actions = actions,
                    modifier =
                        Modifier
                            .navigationBarsPadding()
                            .padding(start = 20.dp, top = 20.dp, end = 20.dp, bottom = 28.dp),
                )
            }
        }
    }
}

@Composable
private fun MoveHeader(
    speciesName: String,
    onCancel: () -> Unit,
) {
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(bottomStart = PANEL_CORNER, bottomEnd = PANEL_CORNER),
        color = MaterialTheme.colorScheme.surface,
        shadowElevation = 2.dp,
    ) {
        Row(
            modifier = Modifier.statusBarsPadding().padding(start = 8.dp, top = 8.dp, end = 8.dp, bottom = 10.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            Box(modifier = Modifier.width(HEADER_SIDE_SLOT), contentAlignment = Alignment.CenterStart) {
                CancelButton(onCancel)
            }
            Column(modifier = Modifier.weight(1f), horizontalAlignment = Alignment.CenterHorizontally) {
                Text(
                    text = speciesName,
                    style = MaterialTheme.typography.labelMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
                Text(
                    text = stringResource(Res.string.treedetail_move_title),
                    style = MaterialTheme.typography.titleLarge,
                )
            }
            Spacer(Modifier.width(HEADER_SIDE_SLOT))
        }
    }
}

/** Grand écran : la colonne de gauche, la carte de droite sert de viseur et porte sa localisation. */
@Composable
private fun MoveTreeExpanded(
    args: MoveTreeArgs,
    candidate: DuplicateCandidate?,
    panel: MovePanelState,
    actions: MovePanelActions,
    onCancel: () -> Unit,
    onOpenTree: (Long) -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier = modifier.fillMaxHeight().verticalScroll(rememberScrollState()).padding(24.dp),
        verticalArrangement = Arrangement.spacedBy(20.dp),
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Text(
                text = stringResource(Res.string.treedetail_move_title_expanded, args.speciesName),
                style = MaterialTheme.typography.labelLarge,
                modifier = Modifier.weight(1f),
            )
            CancelButton(onCancel)
        }
        if (candidate != null) {
            MoveDuplicateBanner(candidate, onOpenTree)
        }
        MovePanel(panel = panel, actions = actions)
    }
}

@Composable
private fun CancelButton(onCancel: () -> Unit) {
    FruxButton(
        text = stringResource(Res.string.treedetail_move_cancel),
        onClick = onCancel,
        style = FruxButtonStyle.Text,
    )
}

@Composable
private fun MoveDuplicateBanner(
    candidate: DuplicateCandidate,
    onOpenTree: (Long) -> Unit,
    modifier: Modifier = Modifier,
) {
    val species = candidate.tree.species
    DuplicateNearbyBanner(
        glyph = glyphFor(species.scientificName, null) ?: FruxSpeciesGlyphs.Fleur,
        tint = speciesFamilyKey(species.scientificName).legendColor(MaterialTheme.fruxColors),
        title =
            stringResource(Res.string.treedetail_move_duplicate_title, species.commonNameFr, candidate.roundedMeters),
        subtitle = stringResource(Res.string.treedetail_move_duplicate_subtitle),
        confirmLabel = stringResource(Res.string.treedetail_move_duplicate_confirm),
        onConfirm = { onOpenTree(candidate.tree.id) },
        modifier = modifier,
    )
}
